import { useState, useCallback, useRef } from 'react';

/**
 * Hook for managing tool calls in chat messages
 * 
 * Handles the lifecycle of tool calls:
 * - Starting tool execution
 * - Tracking progress
 * - Handling results
 * - Error management
 */
export const useToolCalls = () => {
  const [toolCalls, setToolCalls] = useState(new Map()); // messageId -> toolCalls[]
  const toolCallTimeouts = useRef(new Map());

  /**
   * Add a new tool call for a message
   */
  const addToolCall = useCallback((messageId, toolCall) => {
    setToolCalls(prev => {
      const messageCalls = prev.get(messageId) || [];
      const updatedCalls = [...messageCalls, {
        id: toolCall.toolId || `${toolCall.toolName}-${Date.now()}`,
        toolId: toolCall.toolId,
        toolName: toolCall.toolName,
        args: toolCall.args || {},
        status: 'executing',
        statusMessage: toolCall.status || `Executing ${toolCall.toolName}...`,
        startTime: Date.now(),
        connector: toolCall.connector,
        // Position in text where tool was invoked (for inline display during streaming)
        position: toolCall.position ?? 0
      }];
      
      const newMap = new Map(prev);
      newMap.set(messageId, updatedCalls);
      return newMap;
    });

    // Set a timeout to mark as failed if no result comes back
    const timeoutId = setTimeout(() => {
      updateToolCallStatus(messageId, toolCall.toolId || toolCall.toolName, 'error', {
        error: 'Tool execution timeout'
      });
    }, 30000); // 30 second timeout

    toolCallTimeouts.current.set(toolCall.toolId || toolCall.toolName, timeoutId);
  }, []);

  /**
   * Update tool call status and result
   */
  const updateToolCallStatus = useCallback((messageId, toolCallId, status, data = {}) => {
    // Clear timeout if exists
    const timeoutId = toolCallTimeouts.current.get(toolCallId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      toolCallTimeouts.current.delete(toolCallId);
    }

    setToolCalls(prev => {
      const messageCalls = prev.get(messageId) || [];
      const updatedCalls = messageCalls.map(call => {
        if (call.id === toolCallId || call.toolName === toolCallId) {
          return {
            ...call,
            status,
            endTime: Date.now(),
            duration: Date.now() - call.startTime,
            ...data
          };
        }
        return call;
      });
      
      const newMap = new Map(prev);
      newMap.set(messageId, updatedCalls);
      return newMap;
    });
  }, []);

  /**
   * Handle tool call start event from SSE
   */
  const handleToolCallStart = useCallback((messageId, eventData) => {
    addToolCall(messageId, eventData);
  }, [addToolCall]);

  /**
   * Handle tool call result event from SSE
   */
  const handleToolCallResult = useCallback((messageId, eventData) => {
    const status = eventData.success ? 'success' : 'error';
    updateToolCallStatus(messageId, eventData.toolId || eventData.toolName, status, {
      result: eventData.result,
      error: eventData.error,
      connector: eventData.connector,
      statusMessage: eventData.status,
      recordCount: eventData.recordCount
    });
  }, [updateToolCallStatus]);

  /**
   * Get tool calls for a specific message
   */
  const getToolCalls = useCallback((messageId) => {
    return toolCalls.get(messageId) || [];
  }, [toolCalls]);

  /**
   * Check if message has any tool calls
   */
  const hasToolCalls = useCallback((messageId) => {
    const calls = toolCalls.get(messageId) || [];
    return calls.length > 0;
  }, [toolCalls]);

  /**
   * Check if message has any executing tool calls
   */
  const hasExecutingToolCalls = useCallback((messageId) => {
    const calls = toolCalls.get(messageId) || [];
    return calls.some(call => call.status === 'executing');
  }, [toolCalls]);

  /**
   * Get tool call statistics for a message
   */
  const getToolCallStats = useCallback((messageId) => {
    const calls = toolCalls.get(messageId) || [];
    return {
      total: calls.length,
      executing: calls.filter(call => call.status === 'executing').length,
      success: calls.filter(call => call.status === 'success').length,
      error: calls.filter(call => call.status === 'error').length
    };
  }, [toolCalls]);

  /**
   * Retry a failed tool call
   */
  const retryToolCall = useCallback((messageId, toolCall) => {
    // Reset the tool call to executing status
    updateToolCallStatus(messageId, toolCall.id, 'executing', {
      statusMessage: `Retrying ${toolCall.toolName}...`,
      startTime: Date.now(),
      result: null,
      error: null
    });

    // TODO: Implement actual retry logic
    // This would need to communicate with the backend to retry the tool call
    console.log('Tool call retry requested:', { messageId, toolCall });
  }, [updateToolCallStatus]);

  /**
   * Clear tool calls for a message (cleanup)
   */
  const clearToolCalls = useCallback((messageId) => {
    // Clear any pending timeouts
    const calls = toolCalls.get(messageId) || [];
    calls.forEach(call => {
      const timeoutId = toolCallTimeouts.current.get(call.id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        toolCallTimeouts.current.delete(call.id);
      }
    });

    setToolCalls(prev => {
      const newMap = new Map(prev);
      newMap.delete(messageId);
      return newMap;
    });
  }, [toolCalls]);

  /**
   * Clear all tool calls (cleanup)
   */
  const clearAllToolCalls = useCallback(() => {
    // Clear all timeouts
    toolCallTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId));
    toolCallTimeouts.current.clear();
    
    setToolCalls(new Map());
  }, []);

  return {
    // State
    toolCalls,
    
    // Actions
    addToolCall,
    updateToolCallStatus,
    handleToolCallStart,
    handleToolCallResult,
    retryToolCall,
    clearToolCalls,
    clearAllToolCalls,
    
    // Getters
    getToolCalls,
    hasToolCalls,
    hasExecutingToolCalls,
    getToolCallStats
  };
};
